import * as React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

const GOOGLE_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  '333648330110-kqh3pgtr5uft3u5rv29aqtcgsga4gqb6.apps.googleusercontent.com'

/** Redirect URI for GIS ux_mode: 'redirect'. Override via REACT_APP_GOOGLE_LOGIN_URI or REACT_APP_GOOGLE_AUTH_REDIRECT_URI for production. */
const getGoogleLoginUri = (): string =>
  process.env.REACT_APP_GOOGLE_LOGIN_URI ||
  process.env.REACT_APP_GOOGLE_AUTH_REDIRECT_URI ||
  (typeof window !== 'undefined' ? window.location.origin + '/api/v1/user/google-auth/redirect' : '')

const initializeGoogleRedirect = (loginUri: string): void => {
  window.google.accounts.id.initialize({
    client_id: GOOGLE_CLIENT_ID,
    ux_mode: 'redirect',
    login_uri: loginUri,
  })
}

const GoogleButtonWrapper = styled.div`
  width: 100%;
  margin: 16px 0;

  .google-btn-container {
    width: 100%;
    display: flex;
    justify-content: center;
    min-height: 48px;

    /* Fix for Google button duplicate text - hide accessibility-only label */
    #button-label {
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
      border: 0 !important;
    }
  }

  .google-btn-fallback {
    width: 100%;
    height: 48px;
    background-color: #fff;
    border: 1px solid #dadce0;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.2s, box-shadow 0.2s;

    &:hover {
      background-color: #f8f9fa;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .google-icon {
    width: 20px;
    height: 20px;
    margin-right: 12px;
  }

  .google-text {
    font-family: 'Roboto', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #3c4043;
  }

  .divider {
    display: flex;
    align-items: center;
    margin: 20px 0;

    &::before, &::after {
      content: '';
      flex: 1;
      border-bottom: 1px solid #dadce0;
    }

    span {
      padding: 0 16px;
      color: #5f6368;
      font-size: 14px;
    }
  }

  .error-message {
    color: #d93025;
    font-size: 12px;
    margin-top: 8px;
    text-align: center;
  }
`

interface Props {
  onBeforeRedirect?: () => void
  onError?: (error: string) => void
  buttonText?: string
  mode?: 'signin' | 'signup' | 'continue'
  /** Override GIS redirect target (default: REACT_APP_GOOGLE_LOGIN_URI or REACT_APP_GOOGLE_AUTH_REDIRECT_URI or origin + /api/v1/user/google-auth/redirect) */
  loginUri?: string
}

declare global {
  interface Window {
    google: any
    gapi: any
  }
}

export const GoogleAuthButton: React.FC<Props> = ({
  onBeforeRedirect,
  onError,
  buttonText = 'Sign in with Google',
  mode = 'signin',
  loginUri: loginUriProp,
}) => {
  const [error, setError] = useState<string | null>(null)
  const buttonContainerRef = useRef<HTMLDivElement>(null)
  const [gsiLoaded, setGsiLoaded] = useState(false)
  const [buttonRendered, setButtonRendered] = useState(false)
  const loginUri = loginUriProp ?? getGoogleLoginUri()

  const renderGoogleButton = useCallback((): boolean => {
    if (!buttonContainerRef.current) {
      const message = 'Google Sign-In button container is not ready. Please try again.'
      setError(message)
      onError?.(message)
      return false
    }

    if (!window.google?.accounts?.id) {
      return false
    }

    if (!loginUri) {
      const message = 'Google Sign-In redirect URI not configured'
      setError(message)
      onError?.(message)
      return false
    }

    // Diagnostic log - surfaces the exact values sent to Google so a
    // redirect_uri_mismatch can be debugged from the browser console without
    // needing access to the OAuth client error details page. See
    // docs/auth/GOOGLE-OAUTH-VERIFICATION.md.
    // eslint-disable-next-line no-console
    console.info('[Google Sign-In] init', {
      client_id: GOOGLE_CLIENT_ID,
      login_uri: loginUri,
      origin: typeof window !== 'undefined' ? window.location.origin : null,
    })

    try {
      initializeGoogleRedirect(loginUri)
      buttonContainerRef.current.innerHTML = ''

      const textOption = mode === 'signup' ? 'signup_with' : mode === 'continue' ? 'continue_with' : 'signin_with'
      const buttonOptions: Record<string, any> = {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: textOption,
        shape: 'rectangular',
        width: buttonContainerRef.current.offsetWidth || 300,
      }

      if (onBeforeRedirect) {
        buttonOptions.click_listener = onBeforeRedirect
      }

      window.google.accounts.id.renderButton(buttonContainerRef.current, buttonOptions)

      setButtonRendered(true)
      setError(null)
      return true
    } catch (err: any) {
      console.error('Failed to initialize Google Sign-In:', err)
      const message = 'Failed to initialize Google Sign-In'
      setError(message)
      onError?.(message)
      return false
    }
  }, [loginUri, mode, onBeforeRedirect, onError])

  useEffect(() => {
    let checkInterval: NodeJS.Timeout | null = null
    let mounted = true

    // Load Google Identity Services script
    const loadGoogleScript = () => {
      try {
        if (window.google?.accounts?.id) {
          if (mounted) setGsiLoaded(true)
          return
        }

        if (document.getElementById('google-identity-script')) {
          // Script exists but not loaded yet, wait for it
          checkInterval = setInterval(() => {
            if (window.google?.accounts?.id) {
              if (mounted) setGsiLoaded(true)
              if (checkInterval) clearInterval(checkInterval)
            }
          }, 100)
          // Timeout after 10 seconds
          setTimeout(() => {
            if (checkInterval) clearInterval(checkInterval)
          }, 10000)
          return
        }

        const script = document.createElement('script')
        script.id = 'google-identity-script'
        script.src = 'https://accounts.google.com/gsi/client'
        script.async = true
        script.defer = true
        script.onload = () => {
          // Give it a moment to initialize
          setTimeout(() => {
            if (mounted) setGsiLoaded(true)
          }, 100)
        }
        script.onerror = () => {
          if (mounted) setError('Failed to load Google Sign-In')
        }
        document.body.appendChild(script)
      } catch (err) {
        console.error('Error loading Google script:', err)
        if (mounted) setError('Failed to load Google Sign-In')
      }
    }

    loadGoogleScript()

    return () => {
      mounted = false
      if (checkInterval) clearInterval(checkInterval)
    }
  }, [])

  useEffect(() => {
    if (!gsiLoaded || buttonRendered) {
      return
    }

    renderGoogleButton()
  }, [gsiLoaded, buttonRendered, renderGoogleButton])

  const handleFallbackClick = () => {
    if (!loginUri) {
      const message = 'Google Sign-In redirect URI not configured'
      setError(message)
      onError?.(message)
      return
    }

    if (!window.google?.accounts?.id) {
      const message = 'Google Sign-In is still loading. Please try again.'
      setError(message)
      onError?.(message)
      return
    }

    renderGoogleButton()
  }

  // Use separate containers to prevent React/Google DOM conflicts
  // Google's renderButton takes over the container, so we hide the fallback instead of removing it
  return (
    <GoogleButtonWrapper>
      {/* Fallback button - hidden when Google button is rendered */}
      <div
        className="google-btn-container"
        style={{ display: buttonRendered ? 'none' : 'flex' }}
        data-testid="google-auth-fallback-container"
      >
        <button
          className="google-btn-fallback"
          disabled={!loginUri}
          onClick={handleFallbackClick}
          type="button"
          aria-label={buttonText}
          data-testid="google-auth-fallback"
        >
          <svg className="google-icon" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          <span className="google-text">
            {buttonText}
          </span>
        </button>
      </div>
      {/* Google button container - separate from fallback to prevent DOM conflicts */}
      <div
        className="google-btn-container"
        ref={buttonContainerRef}
        style={{ display: buttonRendered ? 'flex' : 'none' }}
        data-testid="google-auth-container"
      />
      {error && <div className="error-message">{error}</div>}
      <div className="divider">
        <span>or</span>
      </div>
    </GoogleButtonWrapper>
  )
}

export default GoogleAuthButton
